import {
  Box,
  Flex,
  Text,
  Spacer,
  useMediaQuery,
  Image,
  // IconButton, // ⛔ Remove IconButton import - no longer needed
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
// ⛔ Remove CloseIcon, HamburgerIcon imports (no longer needed)
import React from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';

// --- CUSTOM ANIMATED HAMBURGER COMPONENT ---
const AnimatedHamburger = ({
  isOpen,
  onClick,
  color = '#b46464',
  size = '30px',
}) => {
  // Common styles for the hamburger lines
  const lineBaseStyles = {
    content: '""',
    display: 'block',
    height: '2.7px',
    position: 'absolute',
    width: '100%',
    borderRadius: '20px',
    background: color,
    left: '0', // Ensure lines are positioned correctly
  };

  return (
    <Box
      as="button"
      onClick={onClick}
      aria-label={isOpen ? 'Close Menu' : 'Open Menu'}
      cursor="pointer"
      display="flex" // Use flex to center the lines vertically
      alignItems="center"
      justifyContent="center"
      height={size} // Container height
      width={size} // Container width
      position="relative"
      zIndex="modal"
      outline="none" // Remove default button outline
      _focus={{ boxShadow: 'outline' }}
      _hover={{ opacity: 0.8 }}
      bg="transparent" // Ensure button background is transparent
    >
      {/* The actual line elements, with pseudo-elements for top and bottom */}
      <Box
        sx={{
          ...lineBaseStyles,
          // Middle line styles
          transition: isOpen
            ? 'background ease 0.3s'
            : 'background ease 0.3s 0.3s',
          background: isOpen ? 'transparent' : color,

          // Rotate for 'X' effect
          transform: isOpen ? 'rotate(45deg)' : 'none',

          _before: {
            ...lineBaseStyles,
            // Top line
            top: isOpen ? '0' : '-8px', // Move to center for 'X'
            transform: isOpen ? 'rotate(-90deg)' : 'none', // Rotate relative to main line
            transition: isOpen
              ? 'top ease 0.3s, transform ease 0.3s 0.3s' // Move, then rotate
              : 'top ease 0.3s 0.3s, transform ease 0.3s', // Rotate, then move
          },
          _after: {
            ...lineBaseStyles,
            // Bottom line
            top: isOpen ? '0' : '8px', // Move to center for 'X'
            transform: isOpen ? 'rotate(-0deg)' : 'none', // Rotate relative to main line
            transition: isOpen
              ? 'top ease 0.3s, transform ease 0.3s 0.3s' // Move, then rotate
              : 'top ease 0.3s 0.3s, transform ease 0.3s', // Rotate, then move
          },
        }}
      />
    </Box>
  );
};

// 1. Define the custom Animated Component (Almost unchanged, removed internal close button)
const AnimatedMenu = ({ isOpen, onClose, navItems }) => {
  // Framer Motion variants for the circular wipe animation
  const menuVariants = {
    hidden: {
      scale: 0,
      transition: { duration: 0.3 },
    },
    visible: {
      scale: 30,
      transition: {
        type: 'spring',
        stiffness: 70,
        damping: 20,
        duration: 0.5,
      },
    },
  };

  // Content variants to fade in AFTER the background wipe
  const contentVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { delay: 0.3, duration: 0.4 },
    },
  };

  // If menu is not open, don't render anything
  if (!isOpen) return null;

  return (
    <Box
      as={motion.div}
      initial="hidden"
      animate="visible"
      exit="hidden"
      position="fixed"
      top="0"
      left="0"
      w="100vw"
      h="100vh"
      zIndex="overlay"
      overflow="hidden"
    >
      {/* The Expanding Circle Background */}
      <Box
        as={motion.div}
        variants={menuVariants}
        transformOrigin="top right"
        w="50px"
        h="50px"
        bg="#b46464"
        // borderRadius="full"
        position="absolute"
        top={{ base: '60px', '2xl': '56px' }} // Position this where the hamburger button is
        right={{ base: '0px', '2xl': '80px' }} // Position this where the hamburger button is
      />

      {/* The Menu Content (Fades in over the circle) */}
      <VStack
        as={motion.div}
        variants={contentVariants}
        spacing={6}
        align="stretch"
        p="40px"
        pt="100px" // Keep padding for aesthetic, but no close button
        w="100%"
        h="100%"
        position="relative"
        justifyContent="center" // Center menu items vertically
      >
        {/* ⛔ Removed the IconButton for closing the menu */}
        {navItems.map(item => (
          <Text
            key={item.path}
            as={NavLink}
            to={item.path}
            fontSize="24px"
            fontWeight="500"
            color="white"
            onClick={onClose} // Still close menu when a link is clicked
            _hover={{ textDecoration: 'underline' }}
            textAlign="center" // Center text content
          >
            {item.label}
          </Text>
        ))}
      </VStack>
    </Box>
  );
};

// 4. Update the main Header component (No changes needed here after AnimatedHamburger fix)
const Header = () => {
  const [isLargerThan768] = useMediaQuery('(min-width: 768px)');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'About Us', path: '/about-us' },
    { label: 'Expertise', path: '/our-expertise' },
    { label: 'Blog', path: '/blog' },
    { label: 'Contact', path: '/get-in-touch' },
  ];

  return (
    <Flex
      as="header"
      align="center"
      justify="space-between"
      wrap="wrap"
      px={{ base: '20px', '2xl': '80px' }}
      py={{ base: '20px', '2xl': '56px' }}
      bg="white"
      color="#b46464"
      position="sticky"
      top="0"
      zIndex="banner"
    >
      <Box>
        <Image src="/image/logo.webp" width={{ base: '150px', md: 'auto' }} />
      </Box>

      <Spacer />

      {isLargerThan768 ? (
        <Flex gap={{ base: '20px', md: '20px', lg: '35px', '2xl': '46px' }}>
          {navItems.map(item => (
            <Text
              key={item.path}
              as={NavLink}
              to={item.path}
              fontSize={{ base: '18px', md: '18px', lg: '20px', '2xl': '24px' }}
              fontWeight="200"
              color="#b46464"
              _hover={{ textDecoration: 'none', color: '#A8463F' }}
              style={({ isActive }) => ({
                color: isActive ? '#A8463F' : '#b46464',
                fontWeight: isActive ? '500' : '200',
              })}
            >
              {item.label}
            </Text>
          ))}
        </Flex>
      ) : (
        <>
          <AnimatedHamburger
            isOpen={isOpen}
            onClick={isOpen ? onClose : onOpen}
            color="#b46464"
            size="30px"
          />

          <AnimatedMenu isOpen={isOpen} onClose={onClose} navItems={navItems} />
        </>
      )}
    </Flex>
  );
};

export default Header;
